import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { API_URL } from "../../lib/utils";
import {
    Globe,
    Shield,
    Server,
    Settings,
    Activity,
    BarChart3,
    Loader2,
    RefreshCw,
    AlertTriangle,
    Users,
    Building2,
    FolderKanban,
    Receipt,
    Brain,
    Mail,
    Calendar,
    DoorOpen,
    Download,
    Database,
    FileText,
} from "lucide-react";

export const AdminSettingsPage = () => {
    const { token } = useAuth();
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [localSettings, setLocalSettings] = useState(null);
    const [exporting, setExporting] = useState(null);

    const fetchData = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const results = await Promise.allSettled([
            fetch(`${API_URL}/platform/settings`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/platform/stats`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
        ]);
        if (results[0].status === "fulfilled") {
            setSettings(results[0].value);
            setLocalSettings(results[0].value);
        }
        if (results[1].status === "fulfilled") setStats(results[1].value);
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSave = async () => {
        if (!localSettings) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/platform/settings`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    maintenance_mode: localSettings.maintenance_mode,
                    registration_enabled: localSettings.registration_enabled,
                    default_plan: localSettings.default_plan,
                    features: localSettings.features,
                }),
            });
            if (!res.ok) throw new Error("Erreur");
            toast.success("Paramètres enregistrés");
            setSettings(localSettings);
        } catch (e) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const toggleFeature = (feature) => {
        setLocalSettings({
            ...localSettings,
            features: {
                ...localSettings.features,
                [feature]: !localSettings.features[feature],
            },
        });
    };

    const hasChanges = JSON.stringify(settings) !== JSON.stringify(localSettings);

    const downloadExport = async (type) => {
        setExporting(type);
        try {
            let endpoint;
            if (type === "db") {
                endpoint = "/platform/exports/database";
            } else if (type === "doc-md") {
                endpoint = "/platform/exports/documentation?format=md";
            } else if (type === "doc-docx") {
                endpoint = "/platform/exports/documentation?format=docx";
            } else if (type === "doc-pdf") {
                endpoint = "/platform/exports/documentation?format=pdf";
            }
            const res = await fetch(`${API_URL}${endpoint}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const disposition = res.headers.get("content-disposition") || "";
            const filenameMatch = disposition.match(/filename="?(.+?)"?$/);
            const filename = filenameMatch ? filenameMatch[1] : "orkestria_export";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Téléchargement terminé");
        } catch {
            toast.error("Erreur lors du téléchargement");
        } finally {
            setExporting(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-settings-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading font-bold text-2xl text-foreground">Configuration</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Paramètres globaux de la plateforme
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-settings-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                    {hasChanges && (
                        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Enregistrer
                        </Button>
                    )}
                </div>
            </div>

            {/* System Stats */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    <Card className="border-violet-200 dark:border-violet-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <Building2 className="w-5 h-5 text-violet-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_tenants}</p>
                                    <p className="text-xs text-muted-foreground">Entreprises</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-blue-200 dark:border-blue-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <Users className="w-5 h-5 text-blue-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_users}</p>
                                    <p className="text-xs text-muted-foreground">Utilisateurs</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-green-200 dark:border-green-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <FolderKanban className="w-5 h-5 text-green-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_projects}</p>
                                    <p className="text-xs text-muted-foreground">Projets</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-orange-200 dark:border-orange-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <Receipt className="w-5 h-5 text-orange-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_invoices}</p>
                                    <p className="text-xs text-muted-foreground">Factures</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-200 dark:border-emerald-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <BarChart3 className="w-5 h-5 text-emerald-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_revenue?.toLocaleString() || 0} €</p>
                                    <p className="text-xs text-muted-foreground">Revenus</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-amber-200 dark:border-amber-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <Activity className="w-5 h-5 text-amber-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats.active_tenants}</p>
                                    <p className="text-xs text-muted-foreground">Actifs</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Platform Info */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                <Globe className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Plateforme</CardTitle>
                                <CardDescription>Informations générales</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Nom</span>
                            <span className="font-medium">{localSettings?.platform_name || "Orkestria"}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Version</span>
                            <Badge variant="outline">{localSettings?.version || "1.5.0"}</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Multi-tenant</span>
                            <Badge className="bg-green-500">Activé</Badge>
                        </div>
                        <div className="border-t pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    <Label htmlFor="maintenance">Mode maintenance</Label>
                                </div>
                                <Switch
                                    id="maintenance"
                                    checked={localSettings?.maintenance_mode || false}
                                    onCheckedChange={(v) => setLocalSettings({ ...localSettings, maintenance_mode: v })}
                                    data-testid="maintenance-mode-switch"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-blue-500" />
                                    <Label htmlFor="registration">Inscriptions ouvertes</Label>
                                </div>
                                <Switch
                                    id="registration"
                                    checked={localSettings?.registration_enabled !== false}
                                    onCheckedChange={(v) => setLocalSettings({ ...localSettings, registration_enabled: v })}
                                    data-testid="registration-switch"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Default Plan */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Settings className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Plans</CardTitle>
                                <CardDescription>Configuration des abonnements</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label className="text-sm text-muted-foreground">Plan par défaut</Label>
                            <Select
                                value={localSettings?.default_plan || "free"}
                                onValueChange={(v) => setLocalSettings({ ...localSettings, default_plan: v })}
                            >
                                <SelectTrigger className="mt-1" data-testid="default-plan-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="free">Free</SelectItem>
                                    <SelectItem value="basic">Basic</SelectItem>
                                    <SelectItem value="pro">Pro</SelectItem>
                                    <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {stats?.plans_distribution && (
                            <div className="pt-2">
                                <Label className="text-sm text-muted-foreground">Distribution actuelle</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {Object.entries(stats.plans_distribution).map(([plan, count]) => (
                                        <Badge key={plan} variant="outline" className="capitalize">
                                            {plan}: {count}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        {localSettings?.limits && (
                            <div className="pt-2">
                                <Label className="text-sm text-muted-foreground">Limites par plan</Label>
                                <div className="mt-2 space-y-2 text-xs">
                                    {Object.entries(localSettings.limits).map(([plan, limits]) => (
                                        <div key={plan} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                                            <Badge variant="outline" className="capitalize w-20">{plan}</Badge>
                                            <span className="text-muted-foreground">
                                                {limits.users === -1 ? "∞" : limits.users} users · 
                                                {limits.projects === -1 ? " ∞" : ` ${limits.projects}`} projets · 
                                                {limits.storage_gb === -1 ? " ∞" : ` ${limits.storage_gb}`} GB
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Features */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <Server className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Fonctionnalités</CardTitle>
                                <CardDescription>Activer/désactiver les modules</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                                <Brain className="w-5 h-5 text-violet-500" />
                                <div>
                                    <p className="text-sm font-medium">Intelligence Artificielle</p>
                                    <p className="text-xs text-muted-foreground">GPT-5.2 pour résumés et analyses</p>
                                </div>
                            </div>
                            <Switch
                                checked={localSettings?.features?.ai_enabled !== false}
                                onCheckedChange={() => toggleFeature("ai_enabled")}
                                data-testid="ai-feature-switch"
                            />
                        </div>
                        <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                                <Mail className="w-5 h-5 text-blue-500" />
                                <div>
                                    <p className="text-sm font-medium">Emails</p>
                                    <p className="text-xs text-muted-foreground">Notifications et rappels</p>
                                </div>
                            </div>
                            <Switch
                                checked={localSettings?.features?.email_enabled !== false}
                                onCheckedChange={() => toggleFeature("email_enabled")}
                                data-testid="email-feature-switch"
                            />
                        </div>
                        <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                                <DoorOpen className="w-5 h-5 text-orange-500" />
                                <div>
                                    <p className="text-sm font-medium">Portail Client</p>
                                    <p className="text-xs text-muted-foreground">Accès externe via magic link</p>
                                </div>
                            </div>
                            <Switch
                                checked={localSettings?.features?.portal_enabled !== false}
                                onCheckedChange={() => toggleFeature("portal_enabled")}
                                data-testid="portal-feature-switch"
                            />
                        </div>
                        <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-green-500" />
                                <div>
                                    <p className="text-sm font-medium">Calendrier</p>
                                    <p className="text-xs text-muted-foreground">Gestion des événements</p>
                                </div>
                            </div>
                            <Switch
                                checked={localSettings?.features?.calendar_enabled !== false}
                                onCheckedChange={() => toggleFeature("calendar_enabled")}
                                data-testid="calendar-feature-switch"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Security */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <Shield className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Sécurité</CardTitle>
                                <CardDescription>Paramètres de sécurité</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Authentification</span>
                            <span className="font-medium">JWT + OAuth + Magic Link</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Isolation des données</span>
                            <Badge className="bg-green-500">Activée</Badge>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Chiffrement</span>
                            <Badge variant="outline">TLS 1.3</Badge>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Sessions JWT</span>
                            <span className="text-xs text-muted-foreground">24h expiration</span>
                        </div>
                        <div className="pt-2">
                            <Label className="text-sm text-muted-foreground">Rôles utilisateur</Label>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                <Badge variant="secondary">platform_owner</Badge>
                                <Badge variant="secondary">admin</Badge>
                                <Badge variant="secondary">manager</Badge>
                                <Badge variant="secondary">employee</Badge>
                                <Badge variant="secondary">client_external</Badge>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Data & Exports */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                            <Database className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Données & Exports</CardTitle>
                            <CardDescription>Télécharger la base de données et la documentation</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <Database className="w-8 h-8 text-indigo-500" />
                                <div>
                                    <p className="font-medium text-sm">Export base de données</p>
                                    <p className="text-xs text-muted-foreground">Toutes les collections MongoDB en JSON</p>
                                </div>
                            </div>
                            <Button
                                onClick={() => downloadExport("db")}
                                disabled={exporting === "db"}
                                className="w-full"
                                variant="outline"
                                data-testid="export-db-btn"
                            >
                                {exporting === "db" ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4 mr-2" />
                                )}
                                Télécharger la base de données
                            </Button>
                        </div>
                        <div className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <FileText className="w-8 h-8 text-emerald-500" />
                                <div>
                                    <p className="font-medium text-sm">Documentation Orkestria</p>
                                    <p className="text-xs text-muted-foreground">Base de connaissance IA</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <Button
                                    onClick={() => downloadExport("doc-md")}
                                    disabled={!!exporting}
                                    variant="outline"
                                    size="sm"
                                    data-testid="export-doc-md-btn"
                                >
                                    {exporting === "doc-md" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                                    .md
                                </Button>
                                <Button
                                    onClick={() => downloadExport("doc-docx")}
                                    disabled={!!exporting}
                                    variant="outline"
                                    size="sm"
                                    data-testid="export-doc-docx-btn"
                                >
                                    {exporting === "doc-docx" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                                    .docx
                                </Button>
                                <Button
                                    onClick={() => downloadExport("doc-pdf")}
                                    disabled={!!exporting}
                                    variant="outline"
                                    size="sm"
                                    data-testid="export-doc-pdf-btn"
                                >
                                    {exporting === "doc-pdf" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                                    .pdf
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Registration Chart */}
            {stats?.monthly_registrations && stats.monthly_registrations.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-violet-500" />
                            Inscriptions mensuelles
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-end gap-2 h-32">
                            {stats.monthly_registrations.map((m, i) => {
                                const maxCount = Math.max(...stats.monthly_registrations.map(x => x.count), 1);
                                const height = (m.count / maxCount) * 100;
                                return (
                                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                        <span className="text-xs font-medium">{m.count}</span>
                                        <div
                                            className="w-full bg-violet-500 rounded-t transition-all duration-300"
                                            style={{ height: `${Math.max(height, 4)}%` }}
                                        />
                                        <span className="text-xs text-muted-foreground">{m.month.split(" ")[0]}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
